import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import './UsuarioModule.css';

interface Usuario {
  cd_usuario: number;
  nm_nome: string;
  nm_username: string;
  nm_email?: string;
  cd_cpf?: string;
  nm_setor?: string;
  nm_funcao?: string;
  nm_acesso?: string;
  cd_ativo: number;
  dt_cadastro?: string;
}

interface Modulo {
  cd_modulo: number;
  nm_modulo: string;
  ds_modulo?: string;
  rota_modulo: string;
  cd_ativo: number;
}

const SETORES_DISPONIVEIS = [
  'Manutenção',
  'TI',
  'Compras',
  'Financeiro',
  'RH',
  'DP',
  'Internacional',
  'Operação Volks',
  'Operação Brose',
  'Operação Eldorado',
  'PGR',
  'Emissão/Faturamento',
  'Segurança',
  'Portaria'
];

const parseSetores = (valor?: string) => {
  if (!valor) return [];
  return Array.from(
    new Set(
      valor
        .split(/[,;|]/)
        .map(setor => setor.trim())
        .filter(Boolean)
    )
  );
};

const UsuarioModule: React.FC = () => {
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [modulos, setModulos] = useState<Modulo[]>([]);
  const [form, setForm] = useState<Omit<Usuario, 'cd_usuario' | 'dt_cadastro'>>({ 
    nm_nome: '', 
    nm_username: '', 
    nm_email: '', 
    cd_cpf: '', 
    nm_setor: '', 
    nm_funcao: '',
    nm_acesso: '',
    cd_ativo: 1 
  });
  const [senha, setSenha] = useState('');
  const [selectedModulos, setSelectedModulos] = useState<number[]>([]);
  const [setoresSelecionados, setSetoresSelecionados] = useState<string[]>([]);
  const [showSetorDropdown, setShowSetorDropdown] = useState(false);
  const setorDropdownRef = useRef<HTMLDivElement | null>(null);
  const [editId, setEditId] = useState<number | null>(null);

  useEffect(() => {
    fetchUsuarios();
    fetchModulos();
  }, []);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!setorDropdownRef.current) return;
      if (!setorDropdownRef.current.contains(event.target as Node)) {
        setShowSetorDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const fetchUsuarios = async () => {
    try {
      const res = await axios.get<Usuario[]>('/api/usuarios');
      setUsuarios(res.data);
    } catch (error) {
      console.error('Erro ao buscar usuários:', error);
    }
  };

  const fetchModulos = async () => {
    try {
      const res = await axios.get<Modulo[]>('/api/modulos');
      setModulos(res.data);
    } catch (error) {
      console.error('Erro ao buscar módulos:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSenhaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSenha(e.target.value);
  };

  const handleSetorToggle = (setor: string, checked: boolean) => {
    if (checked) {
      setSetoresSelecionados(prev => [...prev, setor]);
      return;
    }
    setSetoresSelecionados(prev => prev.filter(s => s !== setor));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Validação obrigatória dos campos
    if (!(form.nm_nome ?? '').trim() ||
      !(form.nm_username ?? '').trim() ||
      !(form.nm_email ?? '').trim() ||
      setoresSelecionados.length === 0 ||
      !(form.nm_funcao ?? '').trim() ||
      !(form.nm_acesso ?? '').trim() ||
      (!editId && !senha.trim())) {
      alert('Preencha todos os campos obrigatórios: Nome, Nome de Usuário, E-mail, Setor, Função, Tipo de Acesso e Senha.');
      return;
    }
    try {
      const userData = {
        ...form,
        nm_setor: setoresSelecionados.join(', '),
        nm_senha: senha,
        modulos: selectedModulos
      };
      if (editId) {
        await axios.put(`/api/usuarios/${editId}`, userData);
      } else {
        await axios.post('/api/usuarios', userData);
      }
      setForm({ nm_nome: '', nm_username: '', nm_email: '', cd_cpf: '', nm_setor: '', nm_funcao: '', nm_acesso: '', cd_ativo: 1 });
      setSenha('');
      setSelectedModulos([]);
      setSetoresSelecionados([]);
      setEditId(null);
      fetchUsuarios();
    } catch (error) {
      console.error('Erro ao salvar usuário:', error);
      const errorMessage = axios.isAxiosError(error)
        ? (error.response?.data?.error || error.message)
        : 'Erro ao salvar usuário';
      alert(errorMessage);
    }
  };

  const handleEdit = async (usuario: Usuario) => {
    setForm({ 
      nm_nome: usuario.nm_nome, 
      nm_username: usuario.nm_username, 
      nm_email: usuario.nm_email || '', 
      cd_cpf: usuario.cd_cpf || '', 
      nm_setor: usuario.nm_setor || '', 
      nm_funcao: usuario.nm_funcao || '',
      nm_acesso: usuario.nm_acesso || '',
      cd_ativo: usuario.cd_ativo 
    });
    setSetoresSelecionados(parseSetores(usuario.nm_setor));
    setSenha(''); // Não preencher senha na edição
    setEditId(usuario.cd_usuario);
    
    // Buscar módulos do usuário
    try {
      const res = await axios.get<number[]>(`/api/usuarios/${usuario.cd_usuario}/modulos`);
      setSelectedModulos(res.data);
    } catch (error) {
      console.error('Erro ao buscar módulos do usuário:', error);
      setSelectedModulos([]);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir este usuário?')) {
      try {
        await axios.delete(`/api/usuarios/${id}`);
        fetchUsuarios();
      } catch (error) {
        console.error('Erro ao excluir usuário:', error);
      }
    }
  };

  const handleModuloChange = (moduloId: number, checked: boolean) => {
    if (checked) {
      setSelectedModulos(prev => [...prev, moduloId]);
    } else {
      setSelectedModulos(prev => prev.filter(id => id !== moduloId));
    }
  };

  const formatarCPF = (cpf: string) => {
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  return (
    <div className="usuario-module">
      <h2>Cadastro de Usuários</h2>
      <form onSubmit={handleSubmit} className="usuario-form">
        <input 
          name="nm_nome" 
          value={form.nm_nome} 
          onChange={handleChange} 
          placeholder="Nome completo" 
          required 
        />
        <input 
          name="nm_username" 
          value={form.nm_username} 
          onChange={handleChange} 
          placeholder="Nome de usuário" 
          required 
        />
        <input 
          name="nm_email" 
          value={form.nm_email} 
          onChange={handleChange} 
          placeholder="E-mail" 
          type="email"
          required
        />
        <input 
          name="cd_cpf" 
          value={form.cd_cpf} 
          onChange={handleChange} 
          placeholder="CPF (somente números)" 
          maxLength={11}
        />
        <div className="setor-dropdown" ref={setorDropdownRef}>
          <button
            type="button"
            className="setor-dropdown-toggle"
            onClick={() => setShowSetorDropdown(prev => !prev)}
            title={setoresSelecionados.length > 0 ? setoresSelecionados.join(', ') : 'Selecione um ou mais setores'}
          >
            <span>
              {setoresSelecionados.length > 0
                ? `${setoresSelecionados.length} setor(es) selecionado(s)`
                : 'Selecione o setor'}
            </span>
            <span className="setor-dropdown-arrow">{showSetorDropdown ? '▲' : '▼'}</span>
          </button>

          {showSetorDropdown && (
            <div className="setor-dropdown-list">
              {SETORES_DISPONIVEIS.map(setor => (
                <label key={setor} className="setor-option">
                  <input
                    type="checkbox"
                    checked={setoresSelecionados.includes(setor)}
                    onChange={(e) => handleSetorToggle(setor, e.target.checked)}
                  />
                  <span>{setor}</span>
                </label>
              ))}
            </div>
          )}
        </div>
        <input 
          name="nm_funcao" 
          value={form.nm_funcao} 
          onChange={handleChange} 
          placeholder="Função" 
          required
        />
        <select
          name="nm_acesso"
          value={form.nm_acesso}
          onChange={handleChange}
          required
        >
          <option value="">Selecione o tipo de acesso</option>
          <option value="Operação">Operacional</option>
          <option value="Gerencial">Gerencial</option>
          <option value="Admin">Admin</option>
        </select>
        <input 
          type="password"
          value={senha} 
          onChange={handleSenhaChange} 
          placeholder={editId ? "Nova senha (opcional)" : "Senha"} 
          required={!editId}
        />
        <select name="cd_ativo" value={form.cd_ativo} onChange={handleChange} required>
          <option value={1}>Ativo</option>
          <option value={0}>Inativo</option>
        </select>
        
        <div className="modulos-section">
          <h4>Módulos de Acesso:</h4>
          <div className="modulos-checkboxes">
            {modulos.map((modulo) => (
              <label key={modulo.cd_modulo} className="checkbox-label">
                <input
                  type="checkbox"
                  checked={selectedModulos.includes(modulo.cd_modulo)}
                  onChange={(e) => handleModuloChange(modulo.cd_modulo, e.target.checked)}
                />
                <span>{modulo.nm_modulo}</span>
              </label>
            ))}
          </div>
        </div>
        
        <div className="usuario-form-actions">
          {editId && (
            <button type="button" onClick={() => { 
              setEditId(null); 
              setForm({ nm_nome: '', nm_username: '', nm_email: '', cd_cpf: '', nm_setor: '', nm_funcao: '', nm_acesso: '', cd_ativo: 1 }); 
              setSenha('');
              setSelectedModulos([]);
              setSetoresSelecionados([]);
            }}>
              Cancelar
            </button>
          )}
          <button type="submit">{editId ? 'Atualizar' : 'Cadastrar'}</button>
        </div>
      </form>

      <div className="usuario-table-wrapper">
        <table className="usuario-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Nome</th>
              <th>Username</th>
              <th>E-mail</th>
              <th>CPF</th>
              <th>Setor</th>
              <th>Função</th>
              <th>Acesso</th>
              <th>Ativo</th>
              <th>Data Cadastro</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {usuarios.map((usuario) => (
              <tr key={usuario.cd_usuario}>
                <td>{usuario.cd_usuario}</td>
                <td>{usuario.nm_nome}</td>
                <td>{usuario.nm_username}</td>
                <td>{usuario.nm_email}</td>
                <td>{usuario.cd_cpf ? formatarCPF(usuario.cd_cpf) : ''}</td>
                <td>{usuario.nm_setor}</td>
                <td>{usuario.nm_funcao}</td>
                <td>{usuario.nm_acesso}</td>
                <td>{usuario.cd_ativo ? 'Sim' : 'Não'}</td>
                <td>{usuario.dt_cadastro ? new Date(usuario.dt_cadastro).toLocaleDateString('pt-BR') : ''}</td>
                <td>
                  <button onClick={() => handleEdit(usuario)}>Editar</button>
                  <button onClick={() => handleDelete(usuario.cd_usuario)}>Excluir</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UsuarioModule;